import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Trophy, Medal, Star, Leaf, Tree, Fire, Planet, Calendar, Users, 
  ArrowsClockwise, Share, TwitterLogo, LinkedinLogo, Check, Lock, Lightning
} from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Leaf: Leaf,
  Tree: Tree,
  TreeEvergreen: Tree,
  Fire: Fire,
  Planet: Planet,
  Calendar: Calendar,
  Users: Users,
  ArrowsClockwise: ArrowsClockwise,
  Trophy: Trophy,
  Star: Star,
  Medal: Medal,
};

const AchievementsPage = () => {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [userAchievements, setUserAchievements] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [challengeProgress, setChallengeProgress] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);

  const irisId = localStorage.getItem('ginsgood_iris_id');
  const irisName = localStorage.getItem('ginsgood_iris_name');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [allAchRes, challengesRes] = await Promise.all([
        axios.get(`${API}/achievements/all`),
        axios.get(`${API}/challenges/active`)
      ]);

      setAchievements(allAchRes.data.achievements);
      setChallenges(challengesRes.data);

      if (irisId) {
        const [userAchRes, progressRes, shareRes] = await Promise.all([
          axios.get(`${API}/achievements/user/${irisId}`),
          axios.get(`${API}/challenges/user/${irisId}/progress`),
          axios.get(`${API}/share/impact/${irisId}`)
        ]);
        setUserAchievements(userAchRes.data);
        setChallengeProgress(progressRes.data);
        setShareData(shareRes.data);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform) => {
    if (!shareData) return;
    
    let url = '';
    if (platform === 'twitter') {
      url = shareData.share.twitter_url;
    } else if (platform === 'linkedin') {
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${encodeURIComponent(shareData.share.linkedin_text)}`;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
    toast.success('Share window opened!');
  };

  const copyShareText = () => {
    if (!shareData) return;
    navigator.clipboard.writeText(shareData.share.text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <div className="animate-pulse">Loading achievements...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-yellow-600 border-yellow-300">
              <Trophy weight="duotone" className="w-4 h-4 mr-2" />
              Achievements & Challenges
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              {irisId ? `${irisName}'s Journey` : 'Climate Action Journey'}
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Earn achievements, complete challenges, and share your impact with the world.
            </p>
          </div>

          {/* Stats Summary (if authenticated) */}
          {userAchievements && (
            <Card className="p-6 mb-8 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200" data-testid="user-stats-card">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <Medal className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
                  <p className="text-2xl font-bold">{userAchievements.earned_count}</p>
                  <p className="text-xs text-muted-foreground">Achievements Earned</p>
                </div>
                <div>
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
                  <p className="text-2xl font-bold">{userAchievements.total_count}</p>
                  <p className="text-xs text-muted-foreground">Total Available</p>
                </div>
                <div>
                  <Lightning className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold">{userAchievements.total_rewards_earned}</p>
                  <p className="text-xs text-muted-foreground">GSG Earned</p>
                </div>
                <div>
                  <Star className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-2xl font-bold">
                    {challengeProgress ? 
                      challengeProgress.weekly.completed_count + challengeProgress.monthly.completed_count : 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Challenges Done</p>
                </div>
              </div>
            </Card>
          )}

          <Tabs defaultValue="achievements" className="space-y-6">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
              <TabsTrigger value="achievements" data-testid="tab-achievements">Achievements</TabsTrigger>
              <TabsTrigger value="challenges" data-testid="tab-challenges">Challenges</TabsTrigger>
              <TabsTrigger value="share" data-testid="tab-share">Share</TabsTrigger>
            </TabsList>

            {/* Achievements Tab */}
            <TabsContent value="achievements">
              {!irisId && (
                <Card className="p-6 mb-6 text-center bg-muted/50">
                  <p className="text-muted-foreground mb-3">Register to start earning achievements!</p>
                  <Button onClick={() => navigate('/iris-auth')}>Get Started</Button>
                </Card>
              )}
              
              <div className="grid md:grid-cols-2 gap-4" data-testid="achievements-grid">
                {(userAchievements?.achievements || achievements).map((ach) => {
                  const Icon = ICON_MAP[ach.icon] || Medal;
                  const isEarned = ach.earned;
                  
                  return (
                    <Card 
                      key={ach.id}
                      className={`p-5 transition-all ${
                        isEarned 
                          ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 shadow-md' 
                          : 'bg-muted/30 opacity-70'
                      }`}
                      data-testid={`achievement-${ach.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                          isEarned 
                            ? 'bg-gradient-to-br from-yellow-400 to-amber-500' 
                            : 'bg-gray-200'
                        }`}>
                          {isEarned ? (
                            <Icon weight="fill" className="w-7 h-7 text-white" />
                          ) : (
                            <Lock className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className={`font-medium ${isEarned ? 'text-yellow-800' : 'text-muted-foreground'}`}>
                              {ach.name}
                            </h3>
                            {isEarned && (
                              <Check weight="bold" className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{ach.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant={isEarned ? "default" : "secondary"} className="text-xs">
                              +{ach.reward_gsg} GSG
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Challenges Tab */}
            <TabsContent value="challenges">
              {!irisId ? (
                <Card className="p-6 text-center bg-muted/50">
                  <p className="text-muted-foreground mb-3">Register to participate in challenges!</p>
                  <Button onClick={() => navigate('/iris-auth')}>Get Started</Button>
                </Card>
              ) : (
                <div className="space-y-8">
                  {/* Weekly Challenges */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <Calendar weight="duotone" className="w-5 h-5 text-blue-500" />
                        Weekly Challenges
                      </h3>
                      <Badge variant="outline">
                        {challenges?.weekly.period.days_remaining} days left
                      </Badge>
                    </div>
                    <div className="grid gap-4" data-testid="weekly-challenges">
                      {challengeProgress?.weekly.progress.map((challenge) => (
                        <Card key={challenge.id} className={`p-5 ${challenge.completed ? 'border-green-200 bg-green-50' : ''}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium">{challenge.name}</h4>
                              <p className="text-sm text-muted-foreground">{challenge.description}</p>
                            </div>
                            <Badge className={challenge.completed ? 'bg-green-500' : 'bg-blue-500'}>
                              {challenge.completed ? 'Complete!' : `+${challenge.reward_gsg} GSG`}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{challenge.current} / {challenge.target}</span>
                              <span>{challenge.progress_percent}%</span>
                            </div>
                            <Progress value={challenge.progress_percent} className="h-2" />
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Monthly Challenges */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <Trophy weight="duotone" className="w-5 h-5 text-purple-500" />
                        Monthly Challenges
                      </h3>
                      <Badge variant="outline">
                        {challenges?.monthly.period.days_remaining} days left
                      </Badge>
                    </div>
                    <div className="grid gap-4" data-testid="monthly-challenges">
                      {challengeProgress?.monthly.progress.map((challenge) => (
                        <Card key={challenge.id} className={`p-5 ${challenge.completed ? 'border-green-200 bg-green-50' : ''}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium">{challenge.name}</h4>
                              <p className="text-sm text-muted-foreground">{challenge.description}</p>
                            </div>
                            <Badge className={challenge.completed ? 'bg-green-500' : 'bg-purple-500'}>
                              {challenge.completed ? 'Complete!' : `+${challenge.reward_gsg} GSG`}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{challenge.current} / {challenge.target}</span>
                              <span>{challenge.progress_percent}%</span>
                            </div>
                            <Progress value={challenge.progress_percent} className="h-2" />
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Rewards Summary */}
                  <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <Lightning weight="duotone" className="w-5 h-5 text-green-600" />
                      Challenge Rewards
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-700">
                          {challengeProgress?.weekly.rewards_earned || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Weekly GSG Earned</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-700">
                          {challengeProgress?.monthly.rewards_earned || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Monthly GSG Earned</p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Share Tab */}
            <TabsContent value="share">
              {!irisId || !shareData ? (
                <Card className="p-6 text-center bg-muted/50">
                  <Share className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mb-3">
                    {!irisId ? 'Register to share your impact!' : 'Start offsetting carbon to unlock sharing!'}
                  </p>
                  <Button onClick={() => navigate(irisId ? '/wallet' : '/iris-auth')}>
                    {irisId ? 'Go to Wallet' : 'Get Started'}
                  </Button>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Impact Card Preview */}
                  <Card className="p-8 bg-gradient-to-br from-green-600 to-emerald-700 text-white" data-testid="share-card">
                    <div className="text-center">
                      <Badge className="bg-white/20 text-white mb-4">{shareData.card_data.badge}</Badge>
                      <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {shareData.card_data.name}
                      </h2>
                      <p className="text-5xl font-bold my-4">{shareData.card_data.total_offset_kg} kg</p>
                      <p className="opacity-80">Carbon Offset</p>
                      
                      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/20">
                        <div>
                          <Tree className="w-6 h-6 mx-auto mb-1" />
                          <p className="font-bold">{shareData.card_data.environmental_equivalents.trees_planted_equivalent}</p>
                          <p className="text-xs opacity-70">Trees</p>
                        </div>
                        <div>
                          <Medal className="w-6 h-6 mx-auto mb-1" />
                          <p className="font-bold">#{shareData.card_data.rank}</p>
                          <p className="text-xs opacity-70">Rank</p>
                        </div>
                        <div>
                          <Trophy className="w-6 h-6 mx-auto mb-1" />
                          <p className="font-bold">{shareData.card_data.achievements_earned}</p>
                          <p className="text-xs opacity-70">Achievements</p>
                        </div>
                      </div>
                      
                      <p className="text-xs opacity-60 mt-4">
                        Member since {shareData.card_data.member_since}
                      </p>
                    </div>
                  </Card>

                  {/* Share Options */}
                  <Card className="p-6">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <Share weight="duotone" className="w-5 h-5" />
                      Share Your Impact
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm mb-3">{shareData.share.text}</p>
                        <Button variant="outline" size="sm" onClick={copyShareText}>
                          Copy Text
                        </Button>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Button 
                          onClick={() => handleShare('twitter')} 
                          className="bg-[#1DA1F2] hover:bg-[#1a8cd8] gap-2"
                          data-testid="share-twitter"
                        >
                          <TwitterLogo weight="fill" className="w-5 h-5" />
                          Share on Twitter
                        </Button>
                        <Button 
                          onClick={() => handleShare('linkedin')} 
                          className="bg-[#0077B5] hover:bg-[#006699] gap-2"
                          data-testid="share-linkedin"
                        >
                          <LinkedinLogo weight="fill" className="w-5 h-5" />
                          Share on LinkedIn
                        </Button>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-4">
                        {shareData.hashtags.map((tag) => (
                          <Badge key={tag} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  </Card>

                  {/* Certificate Download */}
                  <Card className="p-6 text-center">
                    <Trophy className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                    <h3 className="font-medium mb-2">Impact Certificate</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Download your official carbon offset certificate
                    </p>
                    <Button onClick={() => navigate('/wallet')} variant="outline">
                      View Certificate in Wallet
                    </Button>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AchievementsPage;
